import { useEffect, useState } from 'react';

import { clearCache } from '../../services/requestCache';
import { clearTrainerCache } from '../../services/TrainerDashboardService';

/* =============================================================
   PAGE REFRESH

   The topbar's refresh button reloads the page component and nothing
   else: MainLayout keys the routed content on the number kept here, so
   bumping it unmounts the current page and mounts it fresh -- its state
   reset and its data fetched again -- while the topbar, sidebar, open
   widgets, the signed-in session and the URL all stay as they are.

   A fresh mount alone would still be served from caches that outlive
   components, so the refresh clears them first:
   - the shared request cache (services/requestCache.js), all of it,
     and the trainer dashboard service's request de-duplication;
   - any module that keeps its own, by subscribing with onPageRefresh
     and dropping its copy. Listeners run before React re-renders, so
     the page that mounts next already finds them empty.
   ============================================================= */

let version = 0;
const listeners = new Set();

/** Reload the page component that is showing now. */
export const refreshCurrentPage = () => {
  clearCache();
  clearTrainerCache();
  version += 1;
  for (const listener of listeners) {
    try {
      listener(version);
    } catch (error) {
      // One bad listener must not stop the others, or the remount.
      console.error('Page refresh listener failed:', error);
    }
  }
};

/**
 * Be told when the page is refreshed -- for a module-level cache to drop
 * itself. Returns the unsubscribe.
 */
export const onPageRefresh = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** The key MainLayout puts on the routed content. */
export const usePageRefreshKey = () => {
  const [key, setKey] = useState(version);
  useEffect(() => onPageRefresh(setKey), []);
  return key;
};
